import styled from 'styled-components';
import {
    Card,
    CardHeader,
    CardBody,
    FormGroup,
    Input,
    Label,
    Button,
    Row,
    Table,
    Form,
    FormFeedback,
    Alert
} from 'reactstrap';

// Card components
export const AppleCard = styled(Card)`
    border: none;
    border-radius: 12px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    background-color: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    margin-bottom: 24px;
    
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 30px rgba(0, 0, 0, 0.1);
    }
`;

export const AppleCardHeader = styled(CardHeader)`
    background: linear-gradient(to right, #0071e3, #42a1ec);
    border: none;
    padding: 18px 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    h2 {
        color: white;
        font-weight: 500;
        font-size: 18px;
        margin: 0;
        letter-spacing: -0.2px;
    }
`;

export const AppleCardBody = styled(CardBody)`
    padding: 24px;
`;

// Form components
export const FormContent = styled(Row)`
    padding: 24px;
    margin: 0;
`;

export const AppleForm = styled(Form)`
    margin: 0;
`;

export const AppleFormGroup = styled(FormGroup)`
    margin-bottom: 20px;
`;

export const AppleLabel = styled(Label)`
    font-weight: 500;
    color: #333;
    font-size: 14px;
    margin-bottom: 6px;
    display: flex;
    align-items: center;
`;

export const AppleInput = styled(Input)`
    border-radius: 8px;
    padding: 10px 12px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
    font-size: 15px;
    
    &:focus {
        border-color: #0071e3;
        box-shadow: 0 0 0 2px rgba(0, 113, 227, 0.2);
        outline: none;
    }
    
    &::placeholder {
        color: rgba(0, 0, 0, 0.4);
    }
    
    &:disabled {
        background-color: rgba(0, 0, 0, 0.05);
        color: rgba(0, 0, 0, 0.3);
    }
    
    &.is-invalid {
        border-color: #ff3b30;
        box-shadow: 0 0 0 2px rgba(255, 59, 48, 0.2);
    }
`;

export const AppleFormFeedback = styled(FormFeedback)`
    color: #ff3b30;
    font-size: 13px;
    margin-top: 4px;
    font-weight: 400;
`;

export const ErrorMessage = styled.div`
    color: #ff3b30;
    font-size: 13px;
    margin-top: 4px;
    font-weight: 400;
`;

export const InfoMessage = styled.small`
    color: #0071e3;
    margin-top: 6px;
    display: block;
    font-size: 13px;
`;

// Button component
export const AppleButton = styled(Button)`
    background: ${props => props.color === 'danger' 
        ? 'linear-gradient(to right, #ff453a, #ff644f)' 
        : 'linear-gradient(to right, #0071e3, #42a1ec)'};
    border: none;
    border-radius: 8px;
    padding: ${props => props.size === 'sm' ? '6px 12px' : '8px 16px'};
    font-weight: 500;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    
    &:hover, &:focus {
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        background: ${props => props.color === 'danger' 
            ? 'linear-gradient(to right, #ff3b30, #ff5146)' 
            : 'linear-gradient(to right, #0066cc, #0091ff)'};
    }
    
    &:active {
        transform: translateY(0);
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    }
    
    &:disabled {
        opacity: 0.65;
        transform: none !important;
        box-shadow: none !important;
    }
`;

// Table component
export const AppleTable = styled(Table)`
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    
    th {
        background-color: rgba(0, 0, 0, 0.03);
        color: #333;
        font-weight: 600;
        font-size: 14px;
        text-align: left;
        padding: 14px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    }
    
    td {
        padding: 12px 14px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        vertical-align: middle;
    }
    
    tr:last-child td {
        border-bottom: none;
    }
    
    tbody tr {
        transition: background-color 0.2s ease;
        
        &:hover {
            background-color: rgba(0, 113, 227, 0.05);
        }
    }
    
    tr.disabled-row {
        background-color: rgba(0, 0, 0, 0.02);
    }
`;

// Alert component
export const AppleAlert = styled(Alert)`
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    border: none;
    
    &.alert-warning {
        background-color: #fff9e6;
        color: #99540b;
    }
`;

// Section components
export const FormSection = styled.div`
    background-color: rgba(245, 245, 247, 0.6);
    border-radius: 10px;
    padding: 20px;
    margin-bottom: 24px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

export const SectionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    margin-top: 24px;
`;

export const SectionTitle = styled.h5`
    font-weight: 500;
    font-size: 16px;
    color: #333;
    margin: 0;
    display: flex;
    align-items: center;
    
    .text-danger {
        color: #ff3b30;
        margin-left: 4px;
    }
`;

export const Divider = styled.hr`
    border: none;
    height: 1px;
    background-color: rgba(0, 0, 0, 0.1);
    margin: 32px 0;
`;

// React-Select custom styling
export const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        borderRadius: '8px',
        padding: '2px 8px',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        boxShadow: state.isFocused 
            ? '0 0 0 2px rgba(0, 113, 227, 0.2)' 
            : 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
        transition: 'all 0.2s ease',
        fontSize: '15px',
        backgroundColor: state.isDisabled ? '#f2f2f7' : 'rgba(255, 255, 255, 0.8)',
        '&:hover': {
            borderColor: state.isDisabled 
                ? '#d1d1d6' 
                : (state.isFocused ? '#0071e3' : 'rgba(0, 0, 0, 0.2)')
        },
        borderColor: state.isDisabled 
            ? '#d1d1d6' 
            : (state.isFocused ? '#0071e3' : state.selectProps.className?.includes('is-invalid') 
                ? '#ff3b30' 
                : 'rgba(0, 0, 0, 0.1)'),
        opacity: state.isDisabled ? 0.6 : 1,
        cursor: state.isDisabled ? 'not-allowed' : 'default'
    }),
    placeholder: (provided, state) => ({
        ...provided,
        color: state.isDisabled ? '#a2a2a7' : 'rgba(0, 0, 0, 0.4)',
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected 
            ? '#0071e3' 
            : state.isFocused 
                ? 'rgba(0, 113, 227, 0.1)' 
                : 'transparent',
        color: state.isSelected ? 'white' : '#333',
        fontSize: '15px',
        padding: '10px 16px',
    }),
    menu: (provided) => ({
        ...provided,
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        zIndex: 9999,
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999,
    }),
    singleValue: (provided, state) => ({
        ...provided,
        color: state.isDisabled ? '#8e8e93' : '#333',
    }),
    multiValue: (provided) => ({
        ...provided,
        backgroundColor: 'rgba(0, 113, 227, 0.1)',
        borderRadius: '6px',
    }),
    multiValueLabel: (provided) => ({
        ...provided,
        color: '#0071e3',
        fontWeight: 500,
        fontSize: '14px',
    }),
    multiValueRemove: (provided) => ({
        ...provided,
        color: '#0071e3',
        '&:hover': {
            backgroundColor: 'rgba(0, 113, 227, 0.2)',
            color: '#0071e3',
        },
    }),
    dropdownIndicator: (provided, state) => ({
        ...provided,
        color: state.isDisabled ? '#a2a2a7' : '#8e8e93',
        opacity: state.isDisabled ? 0.5 : 1
    }),
    indicatorSeparator: (provided, state) => ({
        ...provided,
        backgroundColor: state.isDisabled ? '#d1d1d6' : '#e5e5ea'
    })
};

// Helper for Select component to ensure dropdowns appear above other elements
export const getSelectProps = () => ({
    styles: customSelectStyles,
    menuPortalTarget: document.body,
    menuPosition: "fixed"
});

// Global CSS fix for React-Select dropdown issues
export const SelectDropdownFix = styled.div`
    /* Global CSS to override any potential issues with React-Select dropdowns */
    & .react-select__menu-portal,
    & .react-select__menu {
        z-index: 9999 !important;
    }
    
    /* Fix for any parent containers that might be hiding overflow */
    & .card,
    & .card-body,
    & .form-group,
    & .row,
    & .col,
    & .container,
    & .container-fluid {
        overflow: visible !important;
    }
    
    /* Ensure the Select component container is visible */
    & .react-select__container {
        position: relative !important;
        z-index: 1 !important;
    }
`;

// Complete styled component that wraps the entire form section to ensure dropdowns work
export const AppleFormWithDropdowns = styled.div`
    position: relative;
    
    /* Force React-Select dropdowns to appear on top */
    .css-26l3qy-menu,
    .css-1n7v3ny-option,
    .css-1pahdxg-control,
    .react-select__menu,
    .react-select__menu-list,
    .react-select__option,
    div[class*="-menu"],
    div[class*="-MenuList"],
    div[class*="-MenuPortal"],
    div[class*="-portal"] {
        z-index: 9999 !important;
    }
    
    /* React-DatePicker specific z-index styles */
    .react-datepicker-popper,
    .react-datepicker__tab-loop,
    .react-datepicker,
    .react-datepicker-wrapper,
    .date-picker-popper {
        z-index: 99999 !important;
    }
    
    .react-datepicker {
        border: 1px solid rgba(0, 0, 0, 0.1) !important;
        border-radius: 12px !important;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important;
        backdrop-filter: blur(10px) !important;
        -webkit-backdrop-filter: blur(10px) !important;
        background-color: rgba(255, 255, 255, 0.95) !important;
        overflow: hidden !important;
        font-family: inherit !important;
    }
    
    .react-datepicker__triangle {
        z-index: 99999 !important;
        border-bottom-color: rgba(255, 255, 255, 0.95) !important;
    }
    
    .react-datepicker__header {
        background-color: rgba(245, 245, 247, 0.8) !important;
        border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
        border-radius: 12px 12px 0 0 !important;
    }
    
    .react-datepicker__day--selected,
    .react-datepicker__day--in-selecting-range,
    .react-datepicker__day--in-range {
        background-color: #0071e3 !important;
        color: white !important;
        border-radius: 6px !important;
    }
    
    .react-datepicker__day--keyboard-selected {
        background-color: rgba(0, 113, 227, 0.2) !important;
        color: #0071e3 !important;
        border-radius: 6px !important;
    }
    
    .react-datepicker__day:hover {
        background-color: rgba(0, 113, 227, 0.1) !important;
        border-radius: 6px !important;
    }
    
    .react-datepicker__day--disabled {
        color: rgba(0, 0, 0, 0.3) !important;
        cursor: not-allowed !important;
    }
    
    .react-datepicker__day--outside-month {
        color: rgba(0, 0, 0, 0.2) !important;
    }
    
    .react-datepicker__current-month,
    .react-datepicker-time__header,
    .react-datepicker-year-header {
        color: #333 !important;
        font-weight: 500 !important;
        font-size: 16px !important;
    }
    
    .react-datepicker__navigation {
        border: none !important;
    }
    
    .react-datepicker__navigation-icon::before {
        border-color: #8e8e93 !important;
    }
    
    .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before {
        border-color: #0071e3 !important;
    }
    
    /* Ensure no overflow hidden in parent containers */
    & > div, 
    & .row, 
    & .col, 
    & .card, 
    & .card-body {
        overflow: visible !important;
    }
`; 
